import { pricing } from "@/stripe/pricing";

export interface CourseBillingConfig {
  courseKey: string;
  coursePath: string;
  currency: string;
  lifetimeEntitlementKind: string;
  lifetimeUnitCount: number;
  passDurationDays: number;
  pricingVersion: string;
  siteDomain: string;
  stripePricingKey: string;
  unitPriceCents: number;
}

export type CourseBillingConfigs = readonly CourseBillingConfig[];

// Keep course products on one payment strategy while site configuration owns
// their identity, catalog key, route, and domain.
//
export const COURSE_ACCESS_UNIT_STRATEGY = {
  currency: "usd",
  lifetimeUnitCount: 5,
  passDurationDays: 30,
  pricingVersion: "usd-10x5",
  unitPriceCents: 1_000,
} as const satisfies Pick<
  CourseBillingConfig,
  | "currency"
  | "lifetimeUnitCount"
  | "passDurationDays"
  | "pricingVersion"
  | "unitPriceCents"
>;

interface CoursePricingInput {
  lifetimeUnitCount?: number;
  passDurationDays?: number;
  unitPriceCents?: number;
}

// Return public prices from either the current offer or a learner's locked
// course-specific cohort.
//
export function getCoursePricingData(
  course: CourseBillingConfig,
  {
    lifetimeUnitCount = course.lifetimeUnitCount,
    passDurationDays = course.passDurationDays,
    unitPriceCents = course.unitPriceCents,
  }: CoursePricingInput = {},
) {
  return {
    lifetimePriceCents: lifetimeUnitCount * unitPriceCents,
    lifetimeUnitCount,
    passDurationDays,
    unitPriceCents,
  };
}

// Resolve only known course keys so Checkout metadata cannot select an
// arbitrary entitlement or ledger.
//
export function getCourseBillingConfig(
  courses: CourseBillingConfigs,
  courseKey: string,
) {
  const course = courses.find((candidate) => {
    return candidate.courseKey === courseKey;
  });

  if (!course) {
    throw new Error(`Unknown course billing key: ${courseKey}.`);
  }

  return course;
}

// Select the Price through an immutable catalog key so Product display names
// remain safe to edit in Stripe.
//
export function getCourseUnitPrice(
  course: CourseBillingConfig,
  courses: CourseBillingConfigs,
) {
  const plans = pricing.filter((plan) => {
    return plan.key === course.stripePricingKey;
  });
  const prices = plans
    .flatMap((plan) => plan.prices)
    .filter((candidate) => {
      return (
        candidate.mode === "payment" &&
        candidate.amount * 100 === course.unitPriceCents
      );
    });
  const price = prices[0];

  if (plans.length !== 1 || prices.length !== 1 || !price) {
    throw new Error(
      `STRIPE_PRICING must contain a ${formatPrice(course.unitPriceCents)} ` +
        `one-time Price for key ${course.stripePricingKey}.`,
    );
  }

  const reusedPrice = courses.find((candidate) => {
    if (candidate.courseKey === course.courseKey) {
      return false;
    }

    return pricing
      .find((plan) => plan.key === candidate.stripePricingKey)
      ?.prices.some((candidatePrice) => candidatePrice.id === price.id);
  });
  if (reusedPrice) {
    throw new Error(
      `${course.courseKey} and ${reusedPrice.courseKey} must use separate ` +
        "Stripe Prices.",
    );
  }

  return price;
}

// Format whole-dollar course prices consistently across pricing and billing.
//
export function formatPrice(cents: number) {
  return `$${(cents / 100).toFixed(0)}`;
}
